import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Navigate, Link, useParams, useSearchParams } from "react-router-dom";
import { Table, Badge } from "react-bootstrap";
import Icon from "react-icons-kit";
import { ic_arrow_back } from "react-icons-kit/md";
import axios from "axios";
import MainWrapper from "../../MainWrapper";
import Loader from "../../layouts/Loader";
import { API_URL } from "../../../config";
import { toast } from "react-toastify";
import {
  resolveItem,
  getYtdRange,
  toApiDate,
  flattenChangeHistory,
  getRowReason,
} from "./customerPerformanceHelpers";
import { isManualRow, buildExclusionPayload } from "./manualPerformanceHelpers";

// Row highlight colors mirror the Existing Orders list: new customers are
// orange, priority (tier > 0) customers are blue.
const ROW_COLORS = {
  newCustomer: { backgroundColor: "#FFE0B2", color: "#5D4037" },
  priority: { backgroundColor: "#BBDEFB", color: "#0D47A1" },
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  const month = d.getMonth() + 1;
  const day = d.getDate();
  return `${month < 10 ? "0" + month : month}-${day < 10 ? "0" + day : day}-${d.getFullYear()}`;
};

// Drill-down view opened from the "Tiered Customer Performance" dashboard
// widget. The route slug (:reason) selects one of the four widget rows; we load
// the matching change-history orders (year-to-date) from GetOrderChangeHistory
// and list them in a read-only table styled like Existing Orders.
const CustomerPerformance = ({ auth: { auth, isAuthenticated } }) => {
  const { reason: slug } = useParams();
  const [searchParams] = useSearchParams();
  const item = resolveItem(slug);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [excluding, setExcluding] = useState(null); // sourceId currently in-flight

  const token = auth && auth[0] ? auth[0].token : null;

  // The widget forwards the row's reason + date window via the query string. Use
  // those when present (changeCategory is always "All" for the forwarded flow);
  // otherwise fall back to the slug's config and a year-to-date range so the
  // page still works when opened directly.
  const queryReason = searchParams.get("reason");
  const queryFromDate = searchParams.get("fromDate");
  const queryToDate = searchParams.get("toDate");
  const queryType = searchParams.get("filterType");

  useEffect(() => {
    if (!token || !item) return;
    setLoading(true);
    const ytd = getYtdRange();
    let tmpreason = queryReason != null ? queryReason : item.reason;
    let reason = tmpreason;
    if(['Quality', 'Shortage'].includes(tmpreason)){
      reason = '';
    }
    let changeCategory = queryReason != null ? "All" : item.changeCategory;
    if(['Quality', 'Shortage'].includes(tmpreason)){
      changeCategory = tmpreason;
    }
    const fromDate =
      (queryFromDate && toApiDate(queryFromDate)) || ytd.fromDate;
    const toDate = (queryToDate && toApiDate(queryToDate)) || ytd.toDate;
    const dataType = queryType || "NonTiered";
    axios
      .get(`${API_URL}/Forecast/api/ForecastTest/GetOrderChangeHistory`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          dataType,
          fromDate,
          toDate,
          reason,
          changeCategory,
        },
      })
      .then((res) => {
        setRows(flattenChangeHistory(res.data));
      })
      .catch((err) => {
        console.warn("Could not load customer performance orders:", err.message);
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [token, slug, queryReason, queryFromDate, queryToDate]);

  // canManage gates the "Remove from Count" column (api.txt #1). Same options
  // endpoint the widget uses; we only need the permission flag here.
  useEffect(() => {
    if (!token) return;
    let active = true;
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/ManualPerformanceOptions`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!active) return;
        const list = (res.data && res.data.result) || [];
        setCanManage(!!(list[0] && list[0].canManage));
      })
      .catch(() => {
        if (!active) return;
        setCanManage(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  // Remove from / restore to the widget counts. Sends the row's own exclusion
  // source (works on system and manual rows). Updates the row in place on
  // success so the button flips without a full reload.
  const handleExclusionToggle = (row) => {
    const nextExcluded = !row.isRemovedFromCount;
    setExcluding(row.exclusionSourceId);
    axios
      .post(
        `${API_URL}/Dashboard/api/Dashboard/SetPerformanceCountExclusion`,
        buildExclusionPayload(row, nextExcluded),
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        const body = res && res.data;
        if (body && String(body.responsecode) !== "200") {
          toast.error(body.error || "Could not update the count.");
          return;
        }
        setRows((prev) =>
          prev.map((r) =>
            r.exclusionSourceType === row.exclusionSourceType &&
            r.exclusionSourceId === row.exclusionSourceId
              ? { ...r, isRemovedFromCount: nextExcluded }
              : r
          )
        );
        toast.success(
          nextExcluded ? "Row omitted from data." : "Row restored to counts."
        );
      })
      .catch((err) => {
        const msg =
          (err.response && err.response.data && err.response.data.error) ||
          "Could not update the count.";
        toast.error(msg);
      })
      .finally(() => setExcluding(null));
  };

  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!item) return <Navigate to="/dashboard" />;

  return (
    <Fragment>
      <MainWrapper>
        <Loader loading={loading}>
          <div className="container">
            <div className="sub-header mt-5 mb-3">
              <h2>{item.title}</h2>
              <Link className="btn btn-primary me-1" to="/dashboard">
                <Icon icon={ic_arrow_back} />Back
              </Link>
            </div>
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>Job Number</th>
                  <th>Property Name</th>
                  <th>Priority</th>
                  <th>Unit Type</th>
                  <th>Installation Date</th>
                  <th>Salesperson</th>
                  <th>Store</th>
                  <th>Reason / Status</th>
                  <th>Manual Entry</th>
                  {canManage ? <th>Remove from Count</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? (
                  rows.map((row, index) => {
                    const isNewCustomer = row.isNewCustomer === true;
                    const tier = row.customerTierNumber;
                    const isPriority = tier != null && tier > 0 && !isNewCustomer;
                    const rowStyle = isNewCustomer
                      ? ROW_COLORS.newCustomer
                      : isPriority
                      ? ROW_COLORS.priority
                      : undefined;
                    return (
                      <tr key={row.jobNo ? `${row.jobNo}-${index}` : index} style={rowStyle}>
                        <td>{row.jobNo || "—"}</td>
                        <td>{row.propertyName || row.jobName || "—"}</td>
                        <td>
                          {isNewCustomer ? (
                            <Badge bg="warning">NEW</Badge>
                          ) : tier != null ? (
                            tier
                          ) : (
                            ""
                          )}
                        </td>
                        <td>{row.unitType || "—"}</td>
                        <td>{formatDate(row.installationDate)}</td>
                        <td>{row.salesPerson || "—"}</td>
                        <td>{row.store || "—"}</td>
                        <td>{getRowReason(row)}</td>
                        <td>
                          {isManualRow(row) ? (
                            <Badge bg="info">Manual</Badge>
                          ) : (
                            "—"
                          )}
                        </td>
                        {canManage ? (
                          <td>
                            {row.isRemovedFromCount ? (
                              <>
                                <span className="text-muted me-2">
                                  Omitted from data
                                </span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  disabled={excluding === row.exclusionSourceId}
                                  onClick={() => handleExclusionToggle(row)}
                                >
                                  Restore
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                disabled={excluding === row.exclusionSourceId}
                                onClick={() => handleExclusionToggle(row)}
                              >
                                Remove from count
                              </button>
                            )}
                          </td>
                        ) : null}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={canManage ? 10 : 9} className="text-center">
                      No orders found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Loader>
      </MainWrapper>
    </Fragment>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export { CustomerPerformance };

export default connect(mapStateToProps)(CustomerPerformance);
